import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
    FaCheck,
    FaChevronDown,
    FaChevronRight,
    FaRegCircle,
    FaTriangleExclamation,
} from "react-icons/fa6";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import { formatCurrency } from "../../../Funcoes/formatCurrency";
import { useModal } from "../../../contexts/ModalContext";
import useApi from "../../../hooks/useApi";
import ModalConciliacaoConta from "./ModalConciliacaoConta";
import ModalSelecionarMes from "./ModalSelecionarMes";
import { flattenAccountTree, getParentContas } from "./accountTree";
import { getContaStatus, STATUS_LABELS } from "./contaStatus";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: ${(p) => (p.$danger ? "rgba(220, 38, 38, 0.15)" : "rgba(255, 255, 255, 0.08)")};
    border: 1px solid ${(p) => (p.$danger ? "rgba(220, 38, 38, 0.5)" : "rgba(255, 255, 255, 0.12)")};
    color: ${(p) => (p.$danger ? "#fca5a5" : "rgba(255, 255, 255, 0.9)")};
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const ActionButton = styled.button`
    height: 40px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(0, 123, 255, 0.85);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const CheckCell = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #16a34a;
`;

const WarnCell = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #dc2626;
`;

const EmptyValueCell = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #eab308;
`;

const ContaCell = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-left: ${(p) => (p.$depth ?? 0) * 16}px;
    font-weight: ${(p) => (p.$isParent ? 600 : 400)};
`;

const TreeToggle = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #555;
    border-radius: 4px;

    &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #007bff;
    }
`;

const TreeSpacer = styled.span`
    display: inline-block;
    width: 18px;
    flex-shrink: 0;
`;

function formatPeriodoLabel(periodo) {
    if (!periodo?.mes || !periodo?.ano) return "-";
    return `${String(periodo.mes).padStart(2, "0")}/${periodo.ano}`;
}

function formatMoney(value) {
    if (value == null || Number.isNaN(Number(value))) return "-";
    return `R$ ${formatCurrency(value)}`;
}

function buildSearchHaystack(row) {
    const status = getContaStatus(row);
    return [
        row.conta,
        row.descricao,
        row.debito,
        row.credito,
        row.movimento_mes,
        row.saldo_anterior,
        row.saldo,
        status === "ok" ? "CONCILIADO" : "",
        status === "incorreto" ? "CONCILIADO INCORRETO DIVERGENTE" : "",
        status === "sem_valor" ? "SEM VALOR ZERADA" : "",
    ]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" ")
        .toUpperCase();
}

const Content = () => {
    const api = useApi();
    const tableRef = useRef(null);
    const { openModal, isModalOpen } = useModal();

    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(null);
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [q, setQ] = useState("");
    const [linhaSelecionada, setLinhaSelecionada] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());
    const [statusFilter, setStatusFilter] = useState("");

    const referenciaAtual = useMemo(() => {
        if (!periodo) return "";
        return `${periodo.ano}-${String(periodo.mes).padStart(2, "0")}`;
    }, [periodo]);

    const fetchMovimentacoes = useCallback(
        async ({ ano, mes }) => {
            setLoading(true);

            try {
                const { data } = await api.get("/Painel/API/Contabilidade/MovimentacaoContas/index.php", {
                    params: { ano, mes: Number(mes) },
                });
                setMovimentacoes(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                setMovimentacoes([]);
            } finally {
                setLoading(false);
            }
        },
        [api]
    );

    const handleConfirmPeriodo = useCallback(
        ({ ano, mes, status = "" }) => {
            const next = { ano: Number(ano), mes: Number(mes) };
            setPeriodo(next);
            setStatusFilter(status);
            fetchMovimentacoes(next);
        },
        [fetchMovimentacoes]
    );

    const handleOpenPeriodoModal = useCallback(() => {
        openModal("selecionarMesContabilidade");
    }, [openModal]);

    const handleRowClick = useCallback(
        (row) => {
            if (!periodo) return;
            setLinhaSelecionada(row);
            openModal("conciliacaoContaContabilidade");
        },
        [openModal, periodo]
    );

    const handleConciliacaoSaved = useCallback(() => {
        if (!periodo) return;
        fetchMovimentacoes(periodo);
    }, [fetchMovimentacoes, periodo]);

    useEffect(() => {
        openModal("selecionarMesContabilidade");
        // Abre apenas na montagem; openModal muda de referência ao fechar o modal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isSearching = q.trim() !== "";
    const hasActiveFilter = isSearching || statusFilter !== "";

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();

        return movimentacoes.filter((row) => {
            if (statusFilter && getContaStatus(row) !== statusFilter) return false;
            if (query && !buildSearchHaystack(row).includes(query)) return false;
            return true;
        });
    }, [movimentacoes, q, statusFilter]);

    // Com filtro ativo (busca ou status), expande tudo para exibir as contas correspondentes.
    const treeRows = useMemo(
        () => flattenAccountTree(filtered, expanded, { expandAll: hasActiveFilter }),
        [filtered, expanded, hasActiveFilter]
    );

    const parentContas = useMemo(() => getParentContas(filtered), [filtered]);

    const allExpanded =
        parentContas.length > 0 && parentContas.every((conta) => expanded.has(conta));

    const toggleExpand = useCallback((conta) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(conta)) {
                next.delete(conta);
            } else {
                next.add(conta);
            }
            return next;
        });
    }, []);

    const handleToggleAll = useCallback(() => {
        setExpanded(() => (allExpanded ? new Set() : new Set(parentContas)));
    }, [allExpanded, parentContas]);

    const totalConciliadas = useMemo(
        () => movimentacoes.filter((row) => row?.conciliacao?.conciliado).length,
        [movimentacoes]
    );

    const totalIncorretas = useMemo(
        () => movimentacoes.filter((row) => getContaStatus(row) === "incorreto").length,
        [movimentacoes]
    );

    const totalSemValor = useMemo(
        () => movimentacoes.filter((row) => getContaStatus(row) === "sem_valor").length,
        [movimentacoes]
    );

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = treeRows.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [treeRows.length]);

    const columns = useMemo(
        () => [
            {
                id: "status",
                header: "",
                accessor: (row) => {
                    const status = getContaStatus(row);
                    if (status === "incorreto") return 3;
                    if (status === "pendente") return 2;
                    if (status === "ok") return 1;
                    return 0;
                },
                sortable: true,
                width: "48px",
                align: "center",
                render: (_, row) => {
                    const status = getContaStatus(row);
                    if (status === "ok") {
                        return (
                            <CheckCell title="Conciliado">
                                <FaCheck size={16} />
                            </CheckCell>
                        );
                    }
                    if (status === "incorreto") {
                        return (
                            <WarnCell title="Conciliado, mas o valor difere do saldo atual">
                                <FaTriangleExclamation size={16} />
                            </WarnCell>
                        );
                    }
                    if (status === "sem_valor") {
                        return (
                            <EmptyValueCell title="Sem nenhum valor">
                                <FaRegCircle size={14} />
                            </EmptyValueCell>
                        );
                    }
                    return null;
                },
            },
            {
                id: "conta",
                header: "CONTA",
                accessor: "conta",
                sortable: true,
                sortType: "string",
                width: "240px",
                render: (value, row) => {
                    const isRowExpanded = hasActiveFilter || expanded.has(row.conta);
                    return (
                        <ContaCell $depth={row.__depth} $isParent={row.__hasChildren}>
                            {row.__hasChildren ? (
                                <TreeToggle
                                    type="button"
                                    title={isRowExpanded ? "Recolher" : "Expandir"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(row.conta);
                                    }}
                                >
                                    {isRowExpanded ? (
                                        <FaChevronDown size={11} />
                                    ) : (
                                        <FaChevronRight size={11} />
                                    )}
                                </TreeToggle>
                            ) : (
                                <TreeSpacer />
                            )}
                            <span>{value}</span>
                        </ContaCell>
                    );
                },
            },
            {
                id: "descricao",
                header: "DESCRIÇÃO",
                accessor: "descricao",
                sortable: true,
            },
            {
                id: "debito",
                header: "DÉBITO",
                accessor: "debito",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "credito",
                header: "CRÉDITO",
                accessor: "credito",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "movimento_mes",
                header: "MOV. MÊS",
                accessor: "movimento_mes",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "saldo_anterior",
                header: "SALDO ANT.",
                accessor: "saldo_anterior",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
            {
                id: "saldo",
                header: "SALDO",
                accessor: "saldo",
                sortable: true,
                align: "right",
                width: "140px",
                render: (value) => formatMoney(value),
            },
        ],
        [expanded, hasActiveFilter, toggleExpand]
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Período: {periodo ? formatPeriodoLabel(periodo) : "Não selecionado"}</Badge>
                <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                <Badge>Conciliadas: {loading ? "..." : totalConciliadas}</Badge>
                <Badge $danger={!loading && totalIncorretas > 0}>
                    Incorretas: {loading ? "..." : totalIncorretas}
                </Badge>
                <Badge>Sem valor: {loading ? "..." : totalSemValor}</Badge>
                {statusFilter && <Badge>Status: {STATUS_LABELS[statusFilter] ?? statusFilter}</Badge>}
                <ActionButton type="button" onClick={handleOpenPeriodoModal}>
                    Alterar filtros
                </ActionButton>
                <ActionButton
                    type="button"
                    onClick={handleToggleAll}
                    disabled={!periodo || hasActiveFilter || parentContas.length === 0}
                >
                    {allExpanded ? "Recolher tudo" : "Expandir tudo"}
                </ActionButton>
                <FilterInput
                    placeholder="Pesquisar por conta, descrição ou valores..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    disabled={!periodo}
                />
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={treeRows}
                        getRowId={(row) => row.conta}
                        isLoading={loading}
                        loadingText="Carregando movimentação contábil..."
                        emptyText={
                            periodo
                                ? "NENHUMA MOVIMENTAÇÃO ENCONTRADA"
                                : "SELECIONE UM PERÍODO PARA CONSULTAR"
                        }
                        initialSort={{ key: "conta", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                        onRowClick={periodo ? handleRowClick : undefined}
                        rowCursor={periodo ? "pointer" : "default"}
                    />
                </TableFlexFill>
            </TableShell>

            {isModalOpen("selecionarMesContabilidade") && (
                <ModalSelecionarMes
                    onConfirm={handleConfirmPeriodo}
                    initialReferencia={referenciaAtual}
                    initialStatus={statusFilter}
                />
            )}

            {isModalOpen("conciliacaoContaContabilidade") && linhaSelecionada && periodo && (
                <ModalConciliacaoConta
                    row={linhaSelecionada}
                    periodo={periodo}
                    onSaved={handleConciliacaoSaved}
                />
            )}
        </PageContent>
    );
};

export default Content;
